import type { Channel } from "phoenix";
import { createEffect, on, untrack } from "solid-js";
import { assertNever, type ChannelEvent, listMembers, ownNickForNetwork } from "./api";
import { socketUserName, token } from "./auth";
import { incrementBadge, setBadge } from "./badge";
import { playBeep } from "./beep";
import { casemappingForSlug } from "./casemapping";
import { type ChannelKey, channelKey, decodeChannelKey } from "./channelKey";
import { dropChannelTopicState, seedModes, seedTopic } from "./channelTopic";
import { isDocumentVisible } from "./documentVisibility";
import { highlightPatterns } from "./highlightList";
import { casemappingForNetwork, isupportEntryFromWire, seedIsupport } from "./isupport";
import { applyPresenceEvent, seedMembers } from "./members";
import { setServerMention } from "./mentions";
import { moduleRoot } from "./moduleRoot";
import {
  channelsBySlug,
  networkBySlug,
  networkIdBySlug,
  networks,
  refetchChannels,
  refetchNetworks,
  user,
} from "./networks";
import { nickEquals } from "./nickEquals";
import { notificationPrefs } from "./notificationPrefs";
import { DEFAULT_NOTIFICATION_SOUND } from "./notificationSound";
import { isOperatorActionEcho } from "./operatorActionEcho";
import { isOwnPresenceEvent } from "./ownPresenceEvent";
import { resolveCtcpReply, resolvePing } from "./pingCorrelation";
import { PRESENCE_COOLDOWN_MS } from "./presenceCooldown";
import { createPresencePause, PRESENCE_PAUSE_ENABLED } from "./presencePause";
import { shouldNotify } from "./pushTriggers";
import { setEnsureQueryTopicJoined } from "./queryTopicJoin";
import { canonicalQueryNick, queryWindowsByNetwork } from "./queryWindows";
import { copyRailWhois } from "./railWhois";
import { applyJoinReply, applyReadCursorSet } from "./readCursor";
import { recordSeen } from "./reconnectBackfill";
import { appendToScrollback, refreshScrollback } from "./scrollback";
import { selectedChannel, setServerSeedCount } from "./selection";
import { type ChannelJoinParams, joinChannel } from "./socket";
import { socketHealth } from "./socketHealth";
import { SERVER_WINDOW_NAME } from "./windowKinds";
import {
  setFailed,
  setJoined,
  setKicked,
  setParted,
  windowIsPresent,
  windowStateByChannel,
} from "./windowState";
import { noteWireDrop } from "./wireDrop";
import { narrowChannelEvent } from "./wireNarrow";

// WS subscription installer. Reactive side-effect module: imports for
// effect, exports nothing public. The app entry (`main.tsx`) imports
// this so the join-effect createRoot evaluates at boot.
//
// Lifted out of the original `networks.ts` god-module per A4. Owns:
//   * `joined` Set — guards double-joins. Phoenix is idempotent on
//     `socket.channel(topic)` (returns the existing handle), but the
//     Set keeps the handler-install step explicit and lets future
//     Phase-5 PART logic mirror with a `leave + delete`.
//   * Three createEffects, sharing one `routeMessage` body:
//     1. Channels loop — iterates `channelsBySlug()`. Subscribes to
//        every real IRC channel topic. Key = channelKey(slug, name).
//     2. Query-windows loop — iterates `queryWindowsByNetwork()`.
//        Subscribes to the per-(slug, targetNick) topic for every
//        open DM window, EXCLUDING targetNick == ownNick (the dm-
//        listener loop owns that topic — see below). The server
//        broadcasts outbound `/msg <nick>` echoes here; subscribing
//        makes those messages appear live in the query pane without
//        a reload (DM live-WS gap fix).
//     3. DM-listener loop — iterates `networks()`. Subscribes to the
//        own-nick topic per network (`grappa:user:<u>/network:<slug>/
//        channel:<ownNick>`). The server broadcasts INBOUND DMs here
//        (the IRC `PRIVMSG <ownNick> :body` line persists with
//        `channel = ownNick`). The handler RE-KEYS the append to
//        the sender's nick — so an incoming reply from `vjt` lands
//        in the `vjt` query window's scrollback, NOT in an invisible
//        own-nick bucket. Self-msg (sender = ownNick, via `/msg
//        <ownNick> :body`) routes to the own-nick key. Always auto-
//        opens the sender's window (idempotent inside queryWindows.ts).
//        Non-PRIVMSG/ACTION events (NOTICE from services, mode, etc.)
//        are DROPPED — they belong in the server-messages window
//        (feature #4, deferred) and must NOT pollute the own-nick key.
//
// All three effects share `routeMessage` so the privmsg/channel
// ingestion paths are byte-identical downstream — per the user's
// directive: "they should be practically the same." Only the iteration
// source and key derivation differ; everything else (scrollback append,
// presence apply, unread split, mention bump) is one code path.
//
// Identity-scoped cleanup mirrors the on(token) arms in `scrollback.ts`
// and `selection.ts`: logout/rotation clears `joined`. Module-import
// order — subscribe imports scrollback + selection + networks — means
// each peer module's createRoot evaluates first and registers its
// cleanup before this one. On a token flush: scrollback cleanup →
// selection cleanup → networks cleanup → subscribe cleanup → the join
// effect re-runs against fresh state once the resources resolve under
// the new bearer.
//
// C3.1: `topic_changed` and `channel_modes_changed` events route to
// `channelTopic.seedTopic` / `channelTopic.seedModes` so TopicBar can
// display live topic + modes without a REST round-trip.
//
// C3.2: JOIN-by-self detection: `message.kind === "join"` events whose
// `sender` matches own nick are forwarded to `joinEvents.notifyJoin`
// so ScrollbackPane can render the one-time join banner.
//
// C4.1 / DM live-WS gap: the auto-open + re-key behaviour for inbound
// DMs lives in the DM-listener loop. Earlier versions tried to detect
// inbound DMs from inside the channels-loop handler by checking
// `name === ownNick`, but that required cicchetto to fake an own-nick
// channel in the channelsBySlug response — which never happens in
// production (channels list is real IRC channels only). The dedicated
// DM-listener loop subscribes to the own-nick topic explicitly and
// re-keys the append to `channelKey(slug, canonicalQueryNick(sender))`
// (#372) so the message lands in the window the user opened, regardless
// of the sender's casing.

// Bucket G H3: the canonical 6-kind WireChannelEvent union now lives
// in `lib/api.ts` next to its sibling `WireUserEvent` (line 381 there).
// Pre-bucket-G this file declared a local `WireEvent` type that ALSO
// duplicated the full union, while api.ts declared a narrow `message`-
// only `ChannelEvent`. Future consumers importing from api.ts were
// type-blind to 5 of 6 arms. Single source removes the drift class.

// #159 E2E gap seam (module-scope so the window hook below can populate
// it and the in-`createRoot` `phx.on` handler can read it). Populated
// ONLY by `__cic_suppressChannelDeliveryForTests`; empty in production.
const suppressedDeliveryKeys = new Set<ChannelKey>();

// #717 — `moduleRoot`, not a bare `createRoot`. The join effects below read
// `user()` and `channelsBySlug()`; in a bare root a throw from an errored boot
// resource escapes `runUpdates`, which discards the queued render effects and so
// denies the render-tree ErrorBoundary the chance to recover. This root handles
// rotation itself via the `joined` map below, so it wants the root and the error
// context, not the identity-reset wiring.
moduleRoot(() => {
  // Codebase review 2026-05-08 cic H2 (HIGH): track Channel objects, not
  // just keys. On rotation we MUST `phx.leave()` each prior Channel
  // before clearing — phoenix.js's `socket.channel(topic)` always
  // creates a NEW Channel and pushes it onto `socket.channels[]`; the
  // OLD Channel and its `phx.on("event", ...)` handler stay alive
  // forever otherwise. Both Channels then receive every dispatched
  // event, doubling presence/unread/mention bumps. N rotations = N+1
  // handlers per channel.
  const joined = new Map<ChannelKey, Channel>();
  // #1680 — the presence pause. A 7-network account takes ~30 events/s and
  // 82% of it is peer join/part/quit for channels nobody is reading; that is
  // what starves the main thread. A channel the user has left alone for
  // `PRESENCE_COOLDOWN_MS` stops applying those, and regaining focus refetches
  // the one store the pause let go stale — the members map.
  //
  // The channel is never ABANDONED: the per-channel topic also carries the
  // messages (`Grappa.Session.Persistor` broadcasts them there), so a paused
  // window must end up subscribed or it goes blind rather than quiet. Since
  // #1769 the pause does call `phx.leave()`, immediately followed by a join
  // carrying the new param — the param is read once at join, so a swap is the
  // only way to change it. The cost of that swap is a short window with no
  // live delivery, closed by the join-ACK `refreshScrollback` that every
  // channel join already runs. See `presencePause.ts` for the full argument
  // and the carve-outs.
  //
  // #1769 wires BOTH edges. `onPause` re-joins the topic with
  // `{presence: false}` so the events stop crossing the socket; `onResume`
  // re-joins without it AND rebuilds the members map. Both handlers reference
  // `rejoinWithPresence`, declared further down: they only ever run from a
  // cooldown timer or a selection change, long after this root has evaluated,
  // and neither can fire during it (no channel is paused at boot).
  const presencePause = createPresencePause(
    {
      onPause: (key) => {
        rejoinWithPresence(key, false);
      },
      onResume: (key) => {
        // Re-subscribe FIRST, then refetch. The other order races a JOIN landing
        // between the two: the refetch would return a member list taken before
        // it, and the event that would have corrected the list arrives while the
        // socket is still suppressing. Subscribing first can only duplicate an
        // apply, which the members store is idempotent under.
        rejoinWithPresence(key, true);
        const t = untrack(token);
        if (!t) return;
        // `decodeChannelKey` is nullable by contract; a key that cannot be
        // decoded cannot be refetched, and inventing a slug would fetch the
        // wrong channel. Leave the members map alone.
        const decoded = decodeChannelKey(key);
        if (decoded === null) return;
        const { slug, name } = decoded;
        // A refetch that throws must not take the handler down with it — the
        // members map simply stays as it was until the next seed. `null` is the
        // server's 204 (`:uninitialized`): joined but pre-NAMES, or not joined.
        // Seeding `[]` there would blank a good list, which is exactly the
        // distinction CP24 bucket E drew.
        void listMembers(t, slug, name)
          .then((members) => {
            if (members !== null) seedMembers(key, members);
          })
          .catch((err: unknown) => {
            console.warn("[subscribe] members refetch failed on resume", key, err);
          });
      },
    },
    PRESENCE_COOLDOWN_MS,
  );

  // Selection IS focus, for the pause's purposes — deliberately NOT gated on
  // `isDocumentVisible()`. A backgrounded tab that comes back must not pay a
  // refetch for every channel it holds; the cooldown already covers genuine
  // abandonment, and tab-switching is the flick case the window exists to
  // absorb. (Issue open question 1, answered this way and recorded as an
  // assumption rather than a measurement.)
  createEffect(() => {
    // #1848 — the kill switch bites HERE, and only here. This is the only
    // caller of `focus`, and `focus` is the only thing that arms a window
    // (`presenceCooldown.blurred`), so returning before it is what "the pause
    // is off" means: nothing is ever added to `pausedKeys`, so `shouldDrop`
    // below is false by construction and `onPause` never re-joins with
    // `{presence: false}`. Both cuts, one branch. See
    // `PRESENCE_PAUSE_ENABLED` in `presencePause.ts` for the why and the
    // revert. The guard sits BEFORE the signal read on purpose: with the
    // switch off this effect runs once, tracks nothing and never wakes again.
    if (!PRESENCE_PAUSE_ENABLED) return;
    const sel = selectedChannel();
    presencePause.focus(
      sel && sel.kind === "channel" ? channelKey(sel.networkSlug, sel.channelName) : null,
    );
  });
  // #254 — coalesce concurrent join callers (the reactive query-windows loop +
  // a compose `/msg` via `ensureQueryTopicJoined`) onto ONE join-ACK promise
  // per query topic, so subscribe-before-send awaits the SAME ack the loop
  // drives (no double-join). Cleared on identity rotation alongside `joined`.
  const queryJoinAcks = new Map<ChannelKey, Promise<void>>();
  // #1341 — per-network provenance for the DM-listener loop: the own-nick key
  // THIS loop joined. `joined` is keyed by topic with no record of who took it,
  // so without this the retiring loop cannot tell its own subscription from a
  // query window that happens to sit on the same key.
  const dmListenerKeys = new Map<string, ChannelKey>();

  createEffect(
    on(token, (t, prev) => {
      if (prev != null && t !== prev) {
        for (const ch of joined.values()) ch.leave();
        joined.clear();
        queryJoinAcks.clear();
        dmListenerKeys.clear();
      }
    }),
  );

  // Effective focus = cicchetto-selected AND browser-tab visible+focused.
  // A selected window in a hidden/blurred tab is NOT being read live —
  // arrivals must accumulate as unread so the marker surfaces on return.
  // Single source for the focus predicate so `routeMessage` (mention
  // path) and the DM-listener call sites (beep dispatch) read the same
  // rule — per CLAUDE.md "Implement once, reuse everywhere". If the
  // rule evolves (page-frozen check, last-seen-window heuristic, etc.)
  // it changes here only.
  const effectivelyFocused = (slug: string, windowName: string): boolean => {
    const sel = untrack(selectedChannel);
    return (
      sel !== null &&
      sel.networkSlug === slug &&
      sel.channelName === windowName &&
      isDocumentVisible()
    );
  };

  // Shared message-routing body. Given a slug + the rendered window's
  // (key, displayName), drives every downstream side-effect for a
  // `kind: "message"` payload: scrollback append, presence apply,
  // unread split, mention bump.
  //
  // `displayName` is the channel-name segment used to compare against
  // selectedChannel.channelName (the rendered window's identity, not
  // necessarily the topic's channel param). For channels and query
  // windows it equals the topic's channel param; for the DM-listener
  // loop it equals the sender's nick (the rendered DM window).
  //
  // `ownNick` gates two BUG5b rules:
  //   - Own presence events (join/part/quit/nick_change from own nick)
  //     never bump unread — the operator drove those actions.
  //   - Own PRIVMSGs/ACTIONs already skip via the isSelected check when
  //     sent from the compose box; the DM-listener re-keys them to the
  //     DM window which may not be selected, but own-sent messages in an
  //     unselected window do still bump (correct — the operator changed
  //     windows between sending and receiving the echo).
  // #591 — a peer's CTCP PING reply arrives as a server-typed :notice with
  // `meta.ctcp_verb === "PING"` + `meta.ctcp_args === <our token>` (the SSOT
  // `Grappa.IRC.CTCP.verb_args/1` classified it; cic NEVER parses \x01). If the
  // token claims a `/ping` WE sent (pingCorrelation), CONSUME it: synthesize the
  // round-trip line into the SOURCE window where /ping was typed (irssi
  // behavior) and swallow the raw reply — no beep, no raw \x01 render. A reply
  // that matches nothing is NOT consumed (returns false): the caller leaves it
  // to normal DM routing, so this stays strictly additive (ruling #2, decoupled
  // from the #546/#548 CTCP-routing work). The RTT body is a cic-owned display
  // string (the server sends structured data only), keyed on the correlation's
  // resolved source window, never the peer's.
  // #719 — every OTHER verb gets the same treatment, keyed on the VERB rather
  // than a token (only PING echoes one). A claim MOVES the row to the source
  // window; no claim leaves it exactly where it was, which is what keeps an
  // unsolicited probe answer visible on `$server` instead of vanishing.
  //
  // The verb folds on BOTH branches. The register side already folded
  // (`sendCtcpQuery`'s `verb.toUpperCase() === "PING"`), so a case-sensitive
  // compare here was an asymmetry: a peer answering `ping` in lower case missed
  // the token table it had a perfectly good entry in. One rule, both ends.
  const maybeConsumeCtcpReply = (slug: string, message: ChannelEvent["message"]): boolean => {
    if (message.kind !== "notice") return false;
    const verb = message.meta.ctcp_verb;
    if (typeof verb !== "string") return false;
    const networkId = networkIdBySlug(slug);
    if (networkId === undefined) return false;

    if (verb.toUpperCase() !== "PING") {
      const claim = resolveCtcpReply(networkId, message.sender, verb);
      if (claim === null) return false;
      // Re-keyed, NOT rewritten: the row keeps its body and its typed ctcp meta
      // so ScrollbackPane's notice arm draws "← CTCP VERSION reply from <peer>"
      // in the source window. Stripping the meta the way the PING arm does would
      // drop it into the generic body render and leak raw \x01 into the DOM
      // (#641). The PING arm can strip because it SYNTHESIZES a human body; here
      // there is nothing to synthesize — the reply already is the answer.
      appendToScrollback(claim.sourceKey, { ...message, channel: claim.sourceChannel });
      return true;
    }

    const token = message.meta.ctcp_args;
    if (typeof token !== "string") return false;
    const hit = resolvePing(networkId, message.sender, token, Date.now());
    if (hit === null) return false;
    appendToScrollback(hit.sourceKey, {
      ...message,
      channel: hit.sourceChannel,
      body: `CTCP PING reply from ${message.sender}: ${hit.rttMs} ms`,
      // #641 — this is a cic-OWNED display row, not the raw CTCP frame. Drop the
      // reply's typed ctcp meta: the body IS the final human line, and carrying
      // meta.ctcp_verb would make ScrollbackPane's notice CTCP arm render it as
      // "← CTCP PING reply from <peer>" and swallow the "N ms" body. The raw
      // reply itself is already swallowed (this branch returns true).
      meta: {},
    });
    return true;
  };

  const routeMessage = (
    slug: string,
    key: ChannelKey,
    displayName: string,
    message: ChannelEvent["message"],
    ownNick: string | null,
  ): void => {
    // #591/#719 — claim a matched CTCP reply before it renders raw in this
    // window. Catches every routing path (channel/query/DM-listener); the
    // DM-listener notice arm ALSO gates earlier to suppress its pre-route beep.
    if (maybeConsumeCtcpReply(slug, message)) return;
    appendToScrollback(key, message);
    // Message-replay-on-reconnect cluster — track high-water mark per
    // topic so the backfill on the NEXT reconnect knows where to
    // resume. recordSeen is the SAME for live + backfilled rows; the
    // high-water mark is "newest row this client has rendered for the
    // topic", not "newest row read by the user". Placed before all
    // unread/focus logic because the cursor is operational metadata,
    // not user-state.
    //
    // NOTE: the topic key the operator subscribed to is NOT necessarily
    // the same as the routing `key`. The DM-listener handler subscribes
    // to the own-nick topic but re-keys the append to the SENDER's key.
    // For refresh-on-join purposes the topic-of-subscription is what we
    // need, because `refreshScrollback(slug, name)` calls
    // `GET .../channels/<topic-channel>/messages?after=<id>` against
    // the topic the WS uses. The DM-listener installs its own
    // subscription on the own-nick topic — that subscription gets its
    // own resume cursor via the recordSeen call inside
    // installDmListenerHandler (see below), keyed on (slug, ownNick).
    // Per-window query topic subscriptions get their cursor here under
    // routeMessage's `key` (which IS the (slug, peer) topic for query
    // windows in the channels/query loops).
    recordSeen(key, message);
    // Members presence delta (P4-1 Q4) — applyPresenceEvent filters
    // by kind: presence kinds (join/part/quit/nick_change/mode/kick)
    // mutate the per-channel member list; content kinds are no-ops
    // there. Dispatching every event keeps routing local to members.ts.
    const casemapping = casemappingForSlug(slug);
    applyPresenceEvent(key, message, casemapping);

    // BUG5b / CP29 R-6: own-action presence events (join/part/quit/
    // nick_change/mode/kick from own nick) must never bump unread — the
    // operator owns those actions and has already seen them. Gate before
    // the isSelected check so own events on both selected and non-
    // selected channels are suppressed. Predicate shared with
    // ScrollbackPane.tsx's in-pane unread-marker filter so the sidebar
    // badge gate and the in-pane marker stay aligned (same single-source
    // pattern as `isOperatorActionEcho`).
    //
    // Post-2026-06-01 (unread-badges-from-cursor cluster, bucket B2):
    // badge counts derive from `(scrollbackByChannel, readCursors,
    // serverSeedCounts)` in `selection.ts` — there are no bump verbs
    // to short-circuit anymore. The own-action gate stays here as the
    // gate for the mention bump path (own /me to a channel that
    // contains your nick mustn't beep / badge yourself) and the early-
    // return short-circuits the dm-listener auto-open logic below.
    if (isOwnPresenceEvent(message, ownNick, casemapping)) return;

    // Server-numeric-derived NOTICE: routed to the window the operator's
    // own action targeted (e.g. /msg <nick> → ERR_NOSUCHNICK 401, persisted
    // as kind:"notice" with meta.numeric=401). Same "operator owns it"
    // semantics as own-presence above — the operator already saw the
    // action that produced this server reply; surfacing it as unread is
    // a false alert. Predicate shared with ScrollbackPane.tsx so the
    // sidebar badge gate and the in-pane unread-marker stay aligned.
    if (isOperatorActionEcho(message)) return;

    // Live notify alert (beep + optimistic PWA badge), for every window shape
    // this function routes — channels, query windows, and the DM listener.
    //
    // #267 — the mention COUNT is not bumped here. It is server-authoritative
    // (`mentions.ts`, seeded by /me + join_reply and pushed on every new
    // message + cursor advance via the `window_counts` event). What stays is
    // the LIVE alert: the in-app beep + the optimistic desktop-title bump the
    // instant a notify-worthy row lands on an unfocused window, before the
    // server's window_counts push round-trips. Transient live cues, not the
    // count of record.
    //
    // #868 — the DECISION is `pushTriggers.shouldNotify`, the mirror of the
    // server's `Grappa.Push.Triggers.should_notify?/5`, and no longer a bare
    // `matchesWatchlist` call. It used to be: this path read NO preference at
    // all, so `channel_mentions: false` still beeped, `private_messages_all:
    // false` still beeped, an `action` the server DID push never moved the
    // title, and `channel_messages_all: true` never beeped. The push and the
    // title answered the same question differently. Routing both through one
    // predicate is what makes a preference mean one thing.
    //
    // The own-echo and kind gates now live INSIDE the predicate (its `own_row`
    // step and `NOTIFY_KINDS`), so they are not repeated here — repeating them
    // is how the two ports drifted in the first place. `ownNick` is non-null
    // on every handler that carries content (the sole `ownNick = null` handler
    // is $server); the guard makes that explicit rather than relying on
    // `nickEquals` returning false for a null side. `u` keeps a not-yet-loaded
    // /me from deciding.
    //
    // Single decision site: the DM listener used to beep at its own call site
    // as well, so a DM whose body contained the operator's nick fired twice.
    //
    // UX-6-L (2026-05-20): the beep complements the SW's
    // visibility-anywhere OS-notification suppression (`lib/pushDedup.ts`).
    const u = untrack(user);
    if (
      u &&
      ownNick !== null &&
      !effectivelyFocused(slug, displayName) &&
      shouldNotify(message, slug, ownNick, notificationPrefs(), highlightPatterns())
    ) {
      // #1480 — WHETHER to alert is this predicate's call; WHICH sound is the
      // subject's preset, and it is passed as data rather than read inside
      // `beep.ts` so the same door serves the settings preview. `none` (the
      // default for everyone) makes `playBeep` a no-op, so the badge still
      // bumps for an opted-out subject: silence is not "no notification".
      playBeep(notificationPrefs().notification_sound ?? DEFAULT_NOTIFICATION_SOUND);
      incrementBadge();
    }
  };

  // Handler for channel-shape topics (real IRC channels + query
  // windows). Topic/modes events seed their respective stores; message
  // events route through `routeMessage` with key = channelKey(slug, name).
  // The C4.1 "auto-open on PRIVMSG to own nick" arm has migrated to
  // the dedicated DM-listener handler — this handler is now purely
  // about its own topic.
  //
  // #200 (2026-07-11): self-JOIN auto-focus is NO LONGER handled here.
  // Focus is per-device, originated at the issuing boundary (compose.ts
  // `/join`, HomePane featured link, ScrollbackPane invite CTA). The
  // per-channel handler must not originate selection — see the message
  // arm below for the full rationale (it's what makes the own-PART
  // teardown safe).
  //
  // BUG5a: self-PART windowState projection. When `message.kind === "part"`
  // and `sender === ownNick`, call `setParted(key)` so the windowState map
  // drops the entry (absence is the projection per CP15 B5). Window
  // dismissal — picking the next focused window — is owned by the UX-4-E
  // close-watcher in `selection.ts:317`: own-PART → `channels_changed`
  // broadcast → `channelsBySlug` drops the channel → close-watcher fires
  // its MRU/server/home picker for the focused-channel case. #200 also
  // tears down the per-channel WS subscription on own-PART (see below).
  //
  // #1340 C-S1: the own nick is resolved PER EVENT, never captured when the
  // topic is joined. `own_nick_changed` → `mutateNetworkNick` re-runs the
  // join effects, but each one is guarded by `joined.has(key)`, so an
  // already-joined channel keeps the handler it was built with — a captured
  // value would answer every own-identity test below against the retired
  // nick for the rest of the session. Re-joining the topics on a rename was
  // rejected: it is heavier than the problem and races the JOIN echo the way
  // #200's revert did. Resolving from the SLUG (not from a captured
  // `Network`) is what makes it live — `mutateNetworkNick` maps the list into
  // fresh row objects, so a closure over the row is exactly as stale as a
  // closure over the string.
  const ownNickForSlug = (slug: string): string | null =>
    untrack(() => {
      const net = networkBySlug(slug);
      return net === undefined ? null : ownNickForNetwork(net, user());
    });

  const installChannelHandler = (
    phx: Channel,
    slug: string,
    name: string,
    key: ChannelKey,
    ownNickAt: () => string | null,
  ) => {
    phx.on("event", (raw: unknown) => {
      const ownNick = ownNickAt();
      // #159 E2E gap seam — a test may silence live delivery for THIS
      // per-channel topic (keyed on `key`) while the socket and every
      // OTHER channel stay live, to reproduce the socket-stays-open
      // per-channel delivery gap that `__cic_dropSocketForTests` cannot
      // (that drops the WHOLE socket, so every channel auto-rejoins and
      // heals via join-ok). The activation/visibility/reconnect freshness
      // re-fetch is the only recovery for this gap class. `suppressedDeliveryKeys`
      // is empty in production — the branch is a dead no-op there.
      if (suppressedDeliveryKeys.has(key)) return;
      // Bucket G H4+U3: runtime narrowing at the WS edge — same
      // boundary-validation pattern as `userTopic.ts`'s
      // `narrowUserEvent` (CP16-era cic M1). phoenix.js types the
      // event payload as JSON-shaped `unknown`; pre-fix this site
      // cast to `WireChannelEvent` directly, which is a *lie* (no
      // runtime enforcement). Malformed payloads (kind valid but a
      // required field missing/wrong-typed) would either crash a
      // setter (`seedTopic(key, undefined)`) or silently corrupt
      // store state. The narrower returns null on any shape
      // mismatch; we drop + log so the operator can investigate
      // without crashing the WS handler.
      const payload = narrowChannelEvent(raw);
      if (payload === null) {
        // #1393d — same class as the user-topic drop, same latch. Wiring
        // only the user topic would have missed `isupport_changed`, which
        // is dual-topic: a stale BEAM drops it on BOTH doors.
        noteWireDrop(raw);
        console.warn("[subscribe] dropped malformed channel payload", raw);
        return;
      }
      // Codebase audit cic M2 — exhaustive switch + `assertNever`
      // mirrors userTopic.ts CP16 B5 pattern. Pre-fix: the handler used
      // an if-else chain ending in `if (payload.kind !== "message")
      // return;` — any new arm added to `WireChannelEvent` (e.g. a
      // future `topic_unset` or `members_delta`) silently dropped at
      // runtime because the catch-all guard didn't surface the new
      // kind. The switch + `assertNever` makes that a `tsc` compile
      // error: the default arm narrows to `never`, so an unhandled
      // kind widens the parameter type and the build fails before the
      // silent drop ships. Note: `mentions_bundle` / `away_confirmed`
      // cited in the audit row are NOT channel-topic events — they
      // fan out on `Topic.user/1` (server.ex:1852, 2190) and are
      // handled by userTopic.ts. Adding them here would be wrong.
      switch (payload.kind) {
        case "topic_changed":
          seedTopic(key, payload.topic);
          return;
        case "channel_modes_changed":
          seedModes(key, payload.modes);
          return;
        case "isupport_changed":
          // #216 — per-network ISUPPORT capability set, seeded into the same
          // store userTopic.ts feeds (keyed by network id, last-write-wins
          // idempotent).
          //
          // #1255 — the CURRENT server does not send this on a channel
          // topic: the cold replay moved to the user-topic snapshot, where a
          // per-network fact belongs. Note for anyone reading this arm as
          // load-bearing for a channel-less client — it is not, and the
          // measurement is worth keeping: THIS handler also serves the
          // `$server` pseudo-window, so the old per-channel replay did reach
          // a client with zero channels, through a window unrelated to the
          // fact it carried. The arm remains for a server older than the
          // bundle (a `--cic`-only deploy), where it is the difference
          // between a seeded table and a dropped payload.
          seedIsupport(payload.network_id, isupportEntryFromWire(payload));
          return;
        // UX-5 BJ (2026-05-19) — recognized-but-ignored. JoinBanner was
        // the only consumer; killed in BJ. Server still emits per-channel
        // on every 329 RPL_CREATIONTIME. Explicit no-op keeps the
        // exhaustive switch + assertNever discipline and prevents the
        // narrower's default-null arm from logging dropped-payload on
        // every JOIN. Server-side reaping is a separate decision.
        case "channel_created":
          return;
        case "members_seeded":
          // Server's 366 RPL_ENDOFNAMES landed and the broadcast carries
          // the full sorted members snapshot. Seed directly — no second
          // fetch needed. Eliminates the WS-subscribed-but-no-fetch-yet
          // race window that an HTTP re-fetch would still be vulnerable
          // to.
          seedMembers(key, payload.members);
          return;
        // CP15 B5: typed window-state events. Same install site as
        // members_seeded so cic flips the rendered window state without
        // polling. setJoined clears any prior failure/kicked metadata
        // for the key so a successful re-join doesn't carry stale
        // by/reason/numeric in the maps.
        //
        // F1 (visitor-parity-and-nickserv 2026-05-15) — live broadcast
        // edge moved to `Topic.user/1` to close the
        // subscribe-then-broadcast race. The per-channel arms below
        // remain the dispatch path for the cold-WS-reconnect snapshot
        // (`push_window_state_if_known/4` pushes via `push(socket,
        // "event", payload)` directly on this channel's socket — no
        // broadcast). Both paths flow through the same setters; both
        // are last-write-wins idempotent so user-topic + per-channel-
        // snapshot dual-arrival on cold reconnect is safe.
        case "joined":
          setJoined(key);
          return;
        case "join_failed":
          setFailed(key, payload.reason, payload.numeric);
          return;
        case "kicked":
          setKicked(key, payload.by, payload.reason);
          return;
        // Cross-device cursor sync. Server emits on every successful
        // `Grappa.ReadCursor.set/4`; route into the signal-map applier.
        case "read_cursor_set":
          applyReadCursorSet(slug, name, payload.last_read_message_id);
          // PWA icon badge door #3: reading anywhere refreshes the
          // server-authoritative count on this client too.
          setBadge(payload.badge_count);
          return;
        // #267 — server-authoritative per-window count snapshot (pushed on
        // new message + cursor advance). cic reads the mention count from
        // it (server owns mentions); messages/events stay client-derived
        // for the presence-filter (#239), so they're ignored here.
        case "window_counts":
          setServerMention(channelKey(slug, payload.channel), payload.mentions);
          return;
        // P-0e + P-0f: invite_ack moved to user-topic; the per-channel
        // arm is gone (and `narrowChannelEvent` no longer narrows the
        // kind so this case is unreachable at the type level).
        case "message": {
          const { message } = payload;

          // #200 (2026-07-11): self-JOIN auto-focus is NO LONGER driven
          // from the per-channel WS handler. Pre-#200 an own-nick JOIN
          // echo here called `setSelectedChannel` (the old "BUG4" path),
          // which entangled focus with per-channel broadcast timing: it
          // (a) forced the S19 own-PART sub-teardown revert (81c0e90a)
          // because a re-JOIN's fresh subscribe raced the JOIN echo and
          // Phoenix doesn't replay to late subscribers, and (b) fanned
          // focus out to EVERY connected device. #200 ruling (b): focus is
          // PER-DEVICE, originated at the issuing boundary — `compose.ts`
          // (`/join`), `HomePane` (featured link), and
          // `ScrollbackPane.handleJoinChannel` (invite CTA) each call
          // `setSelectedChannel` directly. The per-channel handler no
          // longer originates selection; that decoupling is what makes the
          // own-PART teardown below safe.

          // BUG5a: own PART → drop the windowState entry. Selection
          // redirection is owned by the close-watcher in selection.ts
          // (UX-4-E), which fires off the channels_changed broadcast.
          const casemapping = casemappingForSlug(slug);
          const ownPart =
            message.kind === "part" && nickEquals(message.sender, ownNick, casemapping);
          if (ownPart) {
            // CP15 B5: own-PART projects to absence in the windowState
            // map. Server intentionally does NOT broadcast `kind:
            // "parted"` — cic derives the projection here.
            setParted(key);
            // #975: the channel's topic + modes are only observable while
            // we are IN it (the 324 that seeds modes is the server's
            // on-JOIN `MODE #chan` query), so they die with the window.
            // Gated on the OUTCOME of setParted rather than re-deriving
            // its #495 stale-echo guard: when that guard no-ops (a late
            // part echo landing on a re-join already "pending") the key is
            // still present and the freshly-seeded state must survive.
            if (!windowIsPresent(key)) dropChannelTopicState(key);
          }

          // #1680 — the pause bites HERE and nowhere else. `routeMessage` is
          // what applies the members delta (`applyPresenceEvent`) and the
          // scrollback append, so guarding this ONE call drops the noise
          // while every arm around it keeps running: the own-PART teardown
          // above, the #372/#373 nick migration below, and every non-presence
          // kind. `shouldDrop` is false for our own nick and for
          // nick_change/mode by construction.
          if (
            !presencePause.shouldDrop(
              key,
              message.kind,
              nickEquals(message.sender, ownNick, casemapping),
            )
          ) {
            routeMessage(slug, key, name, message, ownNick);
          }

          // A peer's NICK moves no window state here (issue 1365, ruling
          // relayed from vjt, 2026-10-01: a nick change causes no update). The
          // server no longer renames the query window row, so the window, its
          // scrollback and its cursor stay under the old nick, and the renamed
          // peer opens a NEW window the first time it writes. Moving those
          // caches would file the window under a nick the server's
          // `query_windows_list` does not carry — cic originating state. The
          // members map still follows the rename, via `routeMessage` above
          // (`applyPresenceEvent`). What tells the old window where the peer
          // went is a ROW, not cic state: since issue 2368 the server INSERTs
          // one `nick_change` row into the old nick's query window, which
          // reaches this same handler on that window's own topic and lands
          // through `routeMessage` like any other row.
          //
          // The rail's WHOIS cache is the exception, and it is COPIED, not
          // moved: it holds server replies about a person, not window state,
          // and both the old and the new window can put the card on screen.
          // Fires once per shared channel; a second copy is a no-op.
          if (message.kind === "nick_change" && !nickEquals(message.sender, ownNick, casemapping)) {
            const newNick =
              typeof message.meta.new_nick === "string" ? message.meta.new_nick : null;
            if (newNick !== null && !nickEquals(message.sender, newNick, casemapping)) {
              copyRailWhois(slug, message.sender, newNick);
            }
          }

          // #200: tear down the per-channel WS subscription on OWN-part.
          // Pre-#200 `joined` was only `.leave()`d on token rotation, so an
          // own-part left the Phoenix Channel + its `phx.on("event", …)`
          // handler alive on the socket forever — a leak that accumulates
          // over an always-on session that joins/parts many channels. Runs
          // AFTER routeMessage so the PART line still lands in scrollback.
          // Only OWN-part (guarded above) tears down — a peer's PART must
          // NOT drop the sub, or we'd blank a channel we're still in. A
          // subsequent re-JOIN re-subscribes fresh via the pending
          // pre-subscribe loop (its `joined.has(key)` guard sees the
          // delete); the race-free user-topic `window_pending` → `joined`
          // chain (userTopic.ts) drives state recovery, and the join-reply
          // `refreshScrollback` backfills the JOIN row — none of which
          // depend on this per-channel subscription surviving the part.
          if (ownPart) {
            joined.get(key)?.leave();
            joined.delete(key);
          }
          return;
        }
        default:
          assertNever(payload);
      }
    });
  };

  // Handler for the per-network DM-listener (own-nick topic). Every
  // PRIVMSG/ACTION arriving here is either:
  //   (a) an INBOUND DM from `payload.message.sender` (the server
  //       persists `PRIVMSG <ownNick> :body` with `channel = ownNick`),
  //   (b) a self-msg echo (operator issued `/msg <ownNick> :body` —
  //       sender = ownNick).
  // Both are handled uniformly: auto-open the sender's query window
  // (idempotent inside queryWindows.ts) and re-key the append to
  // `channelKey(slug, canonicalQueryNick(sender))` (#372). Self-msg:
  // sender = ownNick → appends to the own-nick key. Inbound: sender =
  // other → appends to the sender's window, resolved to the open
  // window's stored casing so a differently-cased reply doesn't fork.
  //
  // #372 known limitation (strictly narrower than the pre-fix bug): if an
  // inbound reply lands BEFORE the `query_windows_list` broadcast for a
  // just-`/q`'d window arrives, `canonicalQueryNick` can't see the window
  // yet and falls back to the raw sender casing → the reply parks in an
  // in-memory bucket until the next `refreshScrollback` (whose server
  // read path folds, #372). No phantom sidebar row (the server's
  // folded-unique index dedups the open).
  //
  // Non-PRIVMSG/ACTION events on the own-nick topic (NOTICE from
  // services, mode, join, part, etc.) are DROPPED here. They belong
  // in the server-messages window (feature #4, deferred). Silently
  // dropping keeps the own-nick query window clean and avoids
  // polluting any key until the dedicated surface exists.
  const installDmListenerHandler = (
    phx: Channel,
    slug: string,
    networkId: number,
    ownNick: string,
  ) => {
    phx.on("event", (raw: unknown) => {
      // Bucket G H4+U3: same narrower as the channel handler above —
      // see that block for the per-call rationale. The DM-listener
      // shares the same boundary class.
      const payload = narrowChannelEvent(raw);
      if (payload === null) {
        noteWireDrop(raw);
        console.warn("[subscribe] dropped malformed DM payload", raw);
        return;
      }
      // Codebase audit cic M2 — exhaustive switch + `assertNever`.
      // The DM-listener intentionally DROPS every non-message kind
      // (topic_changed/channel_modes_changed make no sense on a nick
      // target, and members_seeded / joined / join_failed / kicked
      // belong to feature #4's deferred server-messages window).
      // Switch over EVERY kind makes that intent explicit and forces
      // future arm additions through this site at `tsc` time.
      switch (payload.kind) {
        case "topic_changed":
        case "channel_modes_changed":
        case "isupport_changed":
        case "channel_created":
        case "members_seeded":
        case "joined":
        case "join_failed":
        case "kicked":
          // Defensive drop — server never emits these on a nick target;
          // even if it did, the DM window's surface is not the right
          // place for them.
          return;
        // Cursor for the own-nick query window. Server emits on every
        // `Grappa.ReadCursor.set/4` just like channel topics; route
        // into the signal map keyed on (slug, ownNick).
        case "read_cursor_set":
          applyReadCursorSet(slug, ownNick, payload.last_read_message_id);
          // PWA icon badge door #3 (own-nick query window path).
          setBadge(payload.badge_count);
          return;
        // #267 — window_counts on the own-nick topic keys the own-nick
        // (self-msg) window. The server's own-nick narrowing restricts
        // that count to self-msgs (inbound peer DMs, dm_with=peer, are
        // excluded), so mentions here are ~always 0. Peer-DM mention
        // counts live on each peer query window's own topic snapshot.
        case "window_counts":
          setServerMention(channelKey(slug, ownNick), payload.mentions);
          return;
        case "message": {
          const message = payload.message;
          if (message.kind === "privmsg" || message.kind === "action") {
            // DM (inbound or self-msg) — route to the sender's scrollback
            // key. For self-msg (sender = ownNick) this lands in the own-nick
            // window; for inbound (sender = other) in the sender's window.
            //
            // #422 Option B: cic does NOT open the query window here. The
            // SERVER auto-opens it next to the persist (Session.Server →
            // QueryWindows.open) and delivers it via the query_windows_list
            // broadcast — "cic NEVER originates state". This arm is now a pure
            // renderer: re-key + append + beep only.
            //
            // #372: re-key on the CANONICAL peer, not the raw sender
            // casing. A service replying as `DebugServ` to a window the
            // user opened as `debugserv` must land in THAT window, not a
            // phantom `DebugServ` duplicate. canonicalQueryNick returns
            // the open window's stored casing (the sidebar/scrollback
            // ChannelKey), or the raw sender on first contact — where the
            // server then mints the window at that same casing, so the key
            // stays consistent. The server folds the DM peer on the read +
            // archive paths too (Scrollback.channel_or_dm_where/list_archive);
            // this is the live-WS mirror of that single window key.
            const peer = canonicalQueryNick(networkId, message.sender);
            const senderKey = channelKey(slug, peer);
            // #868 — no beep at this call site any more. It used to live here
            // "so routeMessage's signature stays narrow", but routeMessage
            // already ran its own mention-path beep over the same row, so a DM
            // mentioning the operator's nick beeped twice. Worse, this site
            // decided "inbound DM is operator-targeted by definition" and so
            // ignored `private_messages_all` / `private_messages_only`
            // entirely, while the server's push obeyed them. routeMessage is
            // now the single decision site and it asks the shared predicate,
            // which routes this row into the DM branch by folded window shape
            // exactly as `Grappa.Push.Triggers.dm?/2` does.
            routeMessage(slug, senderKey, peer, message, ownNick);
            return;
          }
          if (
            message.kind === "notice" &&
            !nickEquals(message.sender, ownNick, casemappingForNetwork(networkId))
          ) {
            // Peer-to-peer NOTICE on the own-nick topic — i.e. landed at
            // `channel == ownNick` because the sender targeted our nick
            // directly. CP23 cluster `code-reload` shipped the canonical
            // case: the CTCP-VERSION-query visibility row (server-emitted
            // notice with body "CTCP VERSION query → grappa <vsn>"; the
            // CTCP reply itself is also a NOTICE so the inbound-side
            // mirror is genuinely a notice, not a privmsg). Same DM-shaped
            // treatment as PRIVMSG/ACTION — the operator wants the same
            // backgrounded-window-with-unread for ANY inbound DM-shaped
            // traffic. #422 Option B: the SERVER auto-opens the window
            // (delivered via query_windows_list); cic only re-keys + appends
            // here (pure renderer), it does NOT originate the window.
            //
            // #546 narrowed WHAT reaches this arm, without changing it: a
            // peer's own NOTICE no longer lands at `channel == ownNick` at
            // all (it routes to `$server`, or to the peer's own topic when
            // that query is already open). No cic edit was needed for #546
            // precisely because this is a renderer — the routing it
            // mirrors moved underneath it.
            //
            // issue 2024 narrowed it AGAIN, and took away the one example
            // the paragraph above used to end on ("what still arrives here
            // is the server-emitted CTCP-query visibility row"). It does
            // not: an inbound CTCP query now goes through the same #546
            // door, so its visibility row lands on `$server` or in the
            // already-open query, never at `channel == ownNick`. No cic
            // edit was needed for that either, for the same reason.
            // ⚠️ NOT claimed: that this arm is now unreachable. Its full
            // input set was not enumerated, so it stays — a renderer arm
            // that fires on nothing costs nothing, and deleting one whose
            // inputs you have not measured is how a class goes silent.
            //
            // sender !== ownNick guard: don't route our OWN outbound NOTICEs
            // (they ride the topic too as fan-out echo). Service-to-self
            // NOTICEs (NickServ etc.) never hit this branch — our server
            // routes those to "$server" not the own-nick channel.
            //
            // #591/#719 — a peer's CTCP reply is DM-shaped (lands here at
            // channel == ownNick). Consume a matched one BEFORE the beep below:
            // routeMessage's own gate swallows the raw render, but this arm
            // beeps PRE-route, so the suppression must happen here too.
            if (maybeConsumeCtcpReply(slug, message)) return;
            // #372: canonical peer re-key — see the PRIVMSG/ACTION arm.
            const peer = canonicalQueryNick(networkId, message.sender);
            const senderKey = channelKey(slug, peer);
            // #868 — a peer NOTICE no longer beeps. UX-6-L gave it the same
            // audio policy as a PRIVMSG on the grounds that it is inbound
            // DM-shaped traffic, but NOTICE is deliberately outside
            // `notify_kinds` on the server (services chatter is the dominant
            // inbound NOTICE shape), so the phone stayed silent while the
            // desktop beeped on the very same row. This is the one
            // user-visible subtraction in #868: the window still takes the
            // unread, only the tone is gone. If peer NOTICEs should be
            // audible, that is a preference, not a hard-coded exception —
            // which is #866's territory, not this refactor's.
            routeMessage(slug, senderKey, peer, message, ownNick);
            return;
          }
          // mode, join, part, quit, kick, nick_change, topic, etc. on
          // the own-nick topic → deferred to feature #4 (server-messages
          // window). Drop silently for now; server-side scrollback row
          // persists at channel=ownNick and will surface when #4 lands.
          return;
        }
        default:
          assertNever(payload);
      }
    });
  };

  // Narrower for the per-channel join reply (`%{read_cursor:
  // <id_or_nil>, window_counts: {messages, mentions, events,
  // severity}}`). phoenix.js delivers the reply as `unknown`-shaped
  // JSON; same boundary-validation pattern as `narrowChannelEvent`.
  // Returns `{cursor, messages, mentions}` with `cursor = null` on
  // missing/invalid shape and zero counts when absent.
  //
  // #267 — the reply carries the full server-authoritative
  // `WindowCounts.snapshot/6` (was the scalar `unread_count`). cic seeds
  // `serverSeedCounts` with `{messages, events}` (message/event counts
  // stay client-derived for the presence-filter, #239 — but the join
  // seed uses the server split as the cold-start value) and the
  // server-authoritative mention count into `mentions.ts`.
  const narrowJoinReply = (
    reply: unknown,
  ): { cursor: number | null; messages: number; events: number; mentions: number } => {
    const zero = { cursor: null, messages: 0, events: 0, mentions: 0 };
    if (typeof reply !== "object" || reply === null) return zero;
    const r = reply as Record<string, unknown>;
    const cursor = typeof r.read_cursor === "number" ? r.read_cursor : null;
    const wc =
      typeof r.window_counts === "object" && r.window_counts !== null
        ? (r.window_counts as Record<string, unknown>)
        : {};
    const num = (v: unknown): number => (typeof v === "number" && v >= 0 ? v : 0);
    return {
      cursor,
      messages: num(wc.messages),
      events: num(wc.events),
      mentions: num(wc.mentions),
    };
  };

  // Single helper called from every per-topic join's reply callback —
  // narrows the reply, applies the cursor to readCursor.ts, seeds
  // selection.ts's per-channel server message/event seed AND the
  // server-authoritative mention count (mentions.ts). Keeps the four
  // identical chains (channels, query, dm-listener, server-window) in
  // one place.
  const applyJoinReplyAndSeed = (slug: string, channelName: string, reply: unknown): void => {
    const { cursor, messages, events, mentions } = narrowJoinReply(reply);
    applyJoinReply(slug, channelName, cursor);
    const key = channelKey(slug, channelName);
    // Always call setServerSeedCount — a 0 seed for a channel with no
    // unread state is informative (it tells the memo "the server says
    // no unread"), and the setter short-circuits on equal-value
    // updates so a 0→0 transition won't re-fire the memo.
    setServerSeedCount(key, { messages, events });
    // #267 — server-authoritative mention count seed.
    setServerMention(key, mentions);
  };

  // #79 — stamp the per-real-channel WS-ready e2e seam after a join ACK.
  // Production never reads it; specs await it (waitForChannelReady) so a
  // selectChannel-then-composeSend flow doesn't race the channel-topic
  // subscribe. Sibling of `stampQueryWindowReady` / `__cic_dmListenerReady`:
  // the server fastlanes the channel own-echo (persist_event → per-channel
  // PubSub broadcast, synchronous on POST — server.ex handle_persisting_send)
  // ONLY to sockets already subscribed; PubSub has NO replay to late
  // subscribers. The pre-#79 heuristic (selectChannel awaiting the self-JOIN
  // scrollback line) proved the initial REST /messages page landed, NOT that
  // the channel `phx.join()` ACK'd — the JOIN line is a boot-persisted row
  // served by REST, so it renders before the WS subscribe completes under
  // full-suite load. Key = the module-native `channelKey(slug, name)` so the
  // await rebuilds the identical composite key.
  const stampChannelReady = (key: ChannelKey): void => {
    if (typeof window === "undefined") return;
    const w = window as Window & { __cic_channelReady?: Set<ChannelKey> };
    if (!w.__cic_channelReady) w.__cic_channelReady = new Set();
    w.__cic_channelReady.add(key);
  };

  // The one door a REAL IRC channel topic is joined through — the channels
  // loop, the pending pre-subscribe loop, and #1769's pause/resume re-join
  // all come here, so the join-ACK obligations (seed, backfill, ready seam)
  // cannot be half-applied by one of them.
  const joinRealChannel = (
    userName: string,
    slug: string,
    name: string,
    key: ChannelKey,
    params: ChannelJoinParams,
  ): void => {
    const phx = joinChannel(
      userName,
      slug,
      name,
      (reply) => {
        applyJoinReplyAndSeed(slug, name, reply);
        // CP29 R-5: refresh on EVERY successful join (initial + every
        // auto-rejoin). This is the ONLY refresh ordered after the
        // subscription is live, so it is the one that closes the
        // subscribe-vs-backfill gap (#1593); the socket-open sweep cannot.
        // The per-key guard inside refreshScrollback keeps bursty rejoins
        // off the wire concurrently and QUEUES the loser (never drops it);
        // the resume-cursor heuristic keeps the fetch short.
        //
        // #1769 leans on this: a pause/resume re-join is a join like any
        // other, so rows that landed while the topic was being swapped are
        // backfilled here rather than by a second mechanism.
        void refreshScrollback(slug, name);
        // #79: this callback fires on the join ACK (subscribed), NOT the
        // `joined.set(key, phx)` below which fires on join-ATTEMPT. Stamp
        // the ready seam here so waitForChannelReady observes a live
        // subscription, never a merely-issued join.
        stampChannelReady(key);
      },
      params,
    );
    installChannelHandler(phx, slug, name, key, () => ownNickForSlug(slug));
    joined.set(key, phx);
  };

  // #1769 — the pause/resume edge, server side. The param is read ONCE by
  // `GrappaWeb.GrappaChannel.join/3`, so changing the answer means joining
  // again; there is deliberately no verb to flip it in place (vjt ruled join
  // params, and a verb would be a second way to say the same thing).
  //
  // Leave-then-join, in that order and both explicit. Phoenix closes the
  // previous channel by itself on a duplicate join, but the stale
  // `phx.on("event", …)` handler on the OLD Channel object would survive on
  // this socket — the cic H2 leak, one extra handler per transition, which on
  // a channel that pauses and resumes all day is unbounded.
  //
  // Called from a timer and from a selection effect, so `untrack`: this must
  // never register `socketUserName` as a dependency of whatever reactive
  // scope happens to be running when a cooldown expires.
  const rejoinWithPresence = (key: ChannelKey, presence: boolean): void => {
    untrack(() => {
      // Not joined (parted, rotated, never seeded) — nothing to re-join, and
      // joining here would resurrect a subscription the #200 own-part
      // teardown deliberately dropped.
      if (!joined.has(key)) return;
      const userName = socketUserName();
      if (!userName) return;
      const decoded = decodeChannelKey(key);
      if (decoded === null) return;
      const { slug, name } = decoded;
      joined.get(key)?.leave();
      joined.delete(key);
      joinRealChannel(userName, slug, name, key, { presence });
    });
  };

  // Channels loop — one join per real IRC channel in channelsBySlug.
  createEffect(() => {
    // Channel topics are addressed by the server's socket-side
    // user_name (set by UserSocket.assign_subject — `"visitor:<uuid>"`
    // for visitors, `User.name` for users). Read via socketUserName()
    // so the visitor topic prefix matches the server-side
    // GrappaChannel.authorize check; pre-C4 cicchetto sent `user.name`
    // for visitors which the server rejected as forbidden — silent
    // root cause of "no networks sidebar for visitors."
    //
    // Track token() explicitly so identity rotation re-runs the
    // effect (socketUserName itself is sync over localStorage and
    // doesn't track on its own).
    const t = token();
    const cbs = channelsBySlug();
    if (!t) return;
    const name = socketUserName();
    const nets = networks();
    if (!name || !cbs) return;
    for (const [slug, list] of Object.entries(cbs)) {
      const net = nets?.find((n) => n.slug === slug) ?? null;
      if (net === null) continue;
      for (const ch of list) {
        const key = channelKey(slug, ch.name);
        if (joined.has(key)) continue;
        joinRealChannel(name, slug, ch.name, key, { presence: true });
      }
    }
  });

  // CP15 B5 fix - pending-channel pre-subscribe loop. Catches the race
  // where the server broadcasts the typed `joined` event (and the
  // subsequent JOIN presence message + channels_changed heartbeat)
  // BEFORE channels_changed triggers the channelsBySlug refetch +
  // channels-loop join. Phoenix PubSub doesn't replay to late
  // subscribers, so without this loop the JOIN events drop on the
  // floor until the next page reload.
  //
  // setPending fires synchronously from compose.ts on `/join`, so this
  // effect re-runs immediately and joins the per-channel topic
  // (typically before the upstream JOIN echo even lands). When
  // channels_changed later fires + channelsBySlug refetches, the
  // channels-loop sees the key in `joined` and skips the duplicate.
  //
  // Same for any other path that sets state to pending (auto-rejoin
  // after disconnect, etc.) - subscribe lives at the WS-topic boundary,
  // not at the user-action boundary.
  createEffect(() => {
    const t = token();
    const states = windowStateByChannel();
    if (!t) return;
    const name = socketUserName();
    const nets = networks();
    if (!name || !nets) return;
    for (const [key, state] of Object.entries(states)) {
      // Pre-subscribe on any NOT-JOINED state that has no other live
      // subscription door: "pending" (our own JOIN in flight, CP15 B5)
      // and "invited" (#78 — inbound INVITE to a channel we're not in).
      // Both need the per-channel topic joined here so the JOIN echo /
      // the persisted INVITE row land. ":failed"/":kicked" already had a
      // subscription (they were joined once); they don't reach this loop
      // needing a fresh join.
      if (state !== "pending" && state !== "invited") continue;
      // Codebase audit cic M4 — paired decoder over open-coded
      // `key.indexOf(" ") + key.slice` parsing. The composite-key
      // shape lives in `channelKey.ts`; the decoder is the inverse
      // of `channelKey(slug, name)`. Sidebar.pseudoChannelsForNetwork
      // is the other consumer.
      const decoded = decodeChannelKey(key as ChannelKey);
      if (decoded === null) continue;
      const slug = decoded.slug;
      const channelName = decoded.name;
      const typedKey = channelKey(slug, channelName);
      if (joined.has(typedKey)) continue;
      const net = nets.find((n) => n.slug === slug) ?? null;
      if (net === null) continue;
      // Same door as the channels loop (`joinRealChannel`), so the #79 ready
      // seam and the join-ACK backfill cannot drift between the two paths.
      // `presence: true` — a window the operator just opened is by definition
      // the one being looked at.
      joinRealChannel(name, slug, channelName, typedKey, { presence: true });
    }
  });

  // #254 — stamp the query-window-ready e2e seam after a (slug,target) join
  // ACK. Production never reads it; specs await it (waitForQueryWindowReady)
  // and the subscribe-before-send probe reads it synchronously at POST time.
  const stampQueryWindowReady = (slug: string, target: string): void => {
    if (typeof window === "undefined") return;
    const w = window as Window & { __cic_queryWindowReady?: Set<string> };
    if (!w.__cic_queryWindowReady) w.__cic_queryWindowReady = new Set();
    w.__cic_queryWindowReady.add(`${slug}/${target}`);
  };

  // #254 — subscribe-before-send: join a query peer's (slug,target) topic and
  // resolve on the join ACK. SINGLE source for BOTH the reactive query-windows
  // loop (fire-and-forget) AND compose's `/msg` (awaited) — so the server's
  // own-echo broadcast has a live listener the instant it fires, WITHOUT an
  // optimistic render. Reuses joinChannel + installChannelHandler + the shared
  // `joined` Map (loop and compose dedup against it → no double-join, no second
  // handler on a topic). Own-nick is skipped: the DM-listener loop is the SOLE
  // subscriber for that topic — a channel handler there would route ALL traffic
  // to the own-nick scrollback key, polluting it (the `joined` guard alone is
  // insufficient because effect evaluation order isn't fixed). `queryJoinAcks`
  // coalesces concurrent callers onto ONE ack. Bounded by
  // `ENSURE_JOIN_ACK_TIMEOUT_MS` so a wedged WS (e.g. #193 WS-blocked-but-
  // REST-up) can't hang the send forever — past the cap the send proceeds and
  // the reconnect self-heal (refreshScrollback on the eventual rejoin) recovers
  // the row. Own-nick comes from `ownNickForSlug` (visitor → me.nick; user →
  // per-credential net.nick), never the displayNick fallback (cic H3).
  const ENSURE_JOIN_ACK_TIMEOUT_MS = 4000;
  const ensureQueryTopicJoined = (slug: string, target: string): Promise<void> => {
    const userName = socketUserName();
    const nets = networks();
    if (!userName || !nets) return Promise.resolve();
    const net = nets.find((n) => n.slug === slug);
    if (!net) return Promise.resolve();
    const ownNick = ownNickForSlug(slug);
    if (nickEquals(target, ownNick, casemappingForSlug(slug))) return Promise.resolve();
    const key = channelKey(slug, target);
    const pending = queryJoinAcks.get(key);
    if (pending) return pending;
    if (joined.has(key)) return Promise.resolve();
    const acked = new Promise<void>((resolve) => {
      // A query window is not a channel: no peer join/part/quit is broadcast
      // on a DM topic, so the presence param would have nothing to suppress.
      const phx = joinChannel(
        userName,
        slug,
        target,
        (reply) => {
          applyJoinReplyAndSeed(slug, target, reply);
          void refreshScrollback(slug, target);
          stampQueryWindowReady(slug, target);
          resolve();
        },
        {},
      );
      installChannelHandler(phx, slug, target, key, () => ownNickForSlug(slug));
      joined.set(key, phx);
    });
    const bounded = Promise.race([
      acked,
      new Promise<void>((resolve) => {
        setTimeout(resolve, ENSURE_JOIN_ACK_TIMEOUT_MS);
      }),
    ]);
    queryJoinAcks.set(key, bounded);
    return bounded;
  };
  // Register the real verb so compose.ts (via the queryTopicJoin leaf) can
  // await it without importing this side-effectful module into its unit tests.
  setEnsureQueryTopicJoined(ensureQueryTopicJoined);

  // Query-windows loop — ensure every open query window's (slug,target) topic
  // is joined so outbound `/msg` echoes AND inbound-DM auto-opened windows flow
  // live without a reload. Re-runs when queryWindowsByNetwork changes;
  // `ensureQueryTopicJoined` is idempotent (the `joined` guard makes a
  // re-run a no-op) and skips own-nick internally (the DM-listener loop owns
  // that topic). Fire-and-forget here — the effect only needs the join started.
  createEffect(() => {
    const t = token();
    const qwbn = queryWindowsByNetwork();
    if (!t) return;
    const nets = networks();
    if (!nets) return;
    for (const [networkIdStr, windowsList] of Object.entries(qwbn)) {
      const net = nets.find((n) => n.id === Number(networkIdStr));
      if (!net) continue;
      for (const qw of windowsList) {
        void ensureQueryTopicJoined(net.slug, qw.targetNick);
      }
    }
  });

  // DM-listener loop — one join per network targeting the own-nick
  // topic. Always-on subscription so inbound DMs from any sender
  // (known or first-contact) are captured + auto-opened + re-keyed.
  // Without this, the first inbound from a new sender would land at
  // a topic nobody is subscribed to and be silently dropped.
  //
  // Joined-set key uses the own-nick key for the network so this
  // subscription is deduped against any future code path that joins
  // the same topic.
  //
  // Own-nick per network: single-source via `ownNickForNetwork(net, me)`.
  // The pre-fix fallback to `displayNick(u)` (= user.name) silently
  // subscribed to the WRONG topic when account-name differed from the
  // IRC nick — see api.ts moduledoc + cic H3.
  //
  // #1341 — the key moves with the nick, so a rename mints a new one and the
  // retired key would stay in `joined` with a live Channel on it. That is not
  // duplicate delivery (a row carries one `channel`, so it reaches one topic)
  // but a SHADOW: once a peer takes the retired nick, our outbound echo to them
  // is broadcast on that key, `ensureQueryTopicJoined` short-circuits on
  // `joined.has(key)` and never installs the channel handler, and this handler
  // re-keys on the sender — so the line lands in our own self window.
  // `dmListenerKeys` records what THIS loop joined, per network, so the release
  // is gated on ownership: a query window opened while we wore another nick can
  // legitimately hold the key we are retiring, and leaving by key would sever it.
  createEffect(() => {
    const t = token();
    const u = user();
    const nets = networks();
    if (!t) return;
    const userName = socketUserName();
    if (!userName || !u || !nets) return;
    for (const net of nets) {
      const ownNick = ownNickForNetwork(net, u);
      if (!ownNick) continue;
      const key = channelKey(net.slug, ownNick);
      const held = dmListenerKeys.get(net.slug);
      if (held !== undefined && held !== key) {
        joined.get(held)?.leave();
        joined.delete(held);
        dmListenerKeys.delete(net.slug);
      }
      // issue 1365 — PRECEDENCE on a contended own-nick topic: the DM listener
      // WINS it, the query loop yields. Two loops can hold this key and only
      // one of them routes correctly: the DM-listener handler re-keys on the
      // SENDER, while `installChannelHandler` keys on the TOPIC — so a query
      // subscription sitting here funnels every inbound DM, from every peer,
      // into the own-nick bucket.
      //
      // The key gets contended because `ensureQueryTopicJoined` skips own-nick
      // against the CURRENT nick: a window opened while we wore another nick
      // keeps the key a later rename makes ours. Pre-fix this arm was a bare
      // `if (joined.has(key)) continue`, so the release above (#1341) handed
      // the old key back and this guard refused the new one — leaving the
      // account with NO DM listener on any topic at all, which is the
      // production symptom.
      //
      // Seizing is safe for the yielding loop: the query-windows effect reads
      // `networks()`, so it re-runs on the very rename that triggered this and
      // re-acquires the key with its own handler once it is no longer our own
      // nick. Clearing `queryJoinAcks` is what keeps that re-acquire from
      // short-circuiting on a stale in-flight ack.
      if (joined.has(key)) {
        if (dmListenerKeys.get(net.slug) === key) continue;
        joined.get(key)?.leave();
        joined.delete(key);
        queryJoinAcks.delete(key);
      }
      // The own-nick DM-listener topic carries inbound DMs, never peer
      // presence — nothing for `{presence: false}` to suppress.
      const phx = joinChannel(
        userName,
        net.slug,
        ownNick,
        (reply) => {
          applyJoinReplyAndSeed(net.slug, ownNick, reply);
          // DM-listener topic refresh: fetches self-msgs only because
          // the controller applies own-nick narrowing when channel ==
          // own_nick (CP14-B3 rule). Inbound peer DMs persist with
          // channel=ownNick AND dm_with=peer; the narrowing filters
          // them out from this fetch by intent (the own-nick query
          // window display would otherwise leak every peer's DMs in).
          // Recovery for inbound peer DMs goes through each open
          // per-peer query window's own refresh — that subscription's
          // rejoin uses the (slug, peer) cursor and the bidirectional
          // DM fetch shape returns BOTH directions. First-contact DMs
          // that arrive during the gap (no per-peer subscription
          // existed yet) are not recovered by this design — deferred
          // edge case, documented here for traceability.
          void refreshScrollback(net.slug, ownNick);
          // UX-6-L e2e seam: stamp the per-slug DM-listener ready set
          // on window after a successful phx.join() ack. Playwright
          // polls `__cic_dmListenerReady?.has(slug)` to await the
          // subscription before driving a peer DM — eliminates the
          // peer-arrives-before-cic-subscribed race (silent broadcast
          // drop) that flaked the ux-6-l e2e ~20% in suite. Production
          // never reads the property; same seam shape as
          // `socket.ts:__cic_dropSocketForTests`. The query-window loop
          // has the SAME no-pre-event-DOM-signal gap (its outbound-echo
          // topic, no self-JOIN line) and carries its own
          // `__cic_queryWindowReady` seam (above); the real-channels loop
          // carries `__cic_channelReady` (stampChannelReady, above) since
          // #79 — the self-JOIN scrollback line is NOT a reliable pre-event
          // signal (it is a boot-persisted row served by the initial REST
          // /messages page, so it renders before the channel `phx.join()`
          // ACKs; the own-echo then fastlanes past the not-yet-subscribed
          // socket and the row never appears). The $server synthetic window
          // is read-only (no compose-then-echo flow) so it needs no seam.
          if (typeof window !== "undefined") {
            const w = window as Window & { __cic_dmListenerReady?: Set<string> };
            if (!w.__cic_dmListenerReady) w.__cic_dmListenerReady = new Set();
            w.__cic_dmListenerReady.add(net.slug);
          }
        },
        {},
      );
      installDmListenerHandler(phx, net.slug, net.id, ownNick);
      joined.set(key, phx);
      dmListenerKeys.set(net.slug, key);
    }
  });

  // BUG2: server-messages loop — one join per network targeting the
  // server-window synthetic channel (SERVER_WINDOW_NAME). The server
  // persists MOTD lines (375/372/376) and server-origin NOTICEs (those
  // addressed to the user's nick, not a channel) to scrollback with
  // channel = SERVER_WINDOW_NAME. Without this subscription, Cicchetto
  // would never receive those events and the :server window stays empty
  // forever.
  //
  // Uses `installChannelHandler` with ownNick=null — no self-JOIN/PART can
  // arrive on this topic, so BUG4/5 detection is intentionally
  // disabled. The cic-side literal lives in windowKinds.ts as a single
  // source matching how the server persists and broadcasts these rows.
  createEffect(() => {
    const t = token();
    const nets = networks();
    if (!t) return;
    const userName = socketUserName();
    if (!userName || !nets) return;
    for (const net of nets) {
      const key = channelKey(net.slug, SERVER_WINDOW_NAME);
      if (joined.has(key)) continue;
      // The synthetic $server window carries MOTD and server NOTICEs; no
      // peer presence is ever broadcast on it, so there is nothing to pause.
      const phx = joinChannel(
        userName,
        net.slug,
        SERVER_WINDOW_NAME,
        (reply) => {
          applyJoinReplyAndSeed(net.slug, SERVER_WINDOW_NAME, reply);
          void refreshScrollback(net.slug, SERVER_WINDOW_NAME);
        },
        {},
      );
      installChannelHandler(phx, net.slug, SERVER_WINDOW_NAME, key, () => null);
      joined.set(key, phx);
    }
  });

  // Message-replay-on-reconnect cluster bonus — defensive resync on
  // socket-open transitions. Refetches `networks` + `channelsBySlug`
  // so the channels-loop createEffect re-runs against fresh server
  // state. Catches the gap class where:
  //
  //   1. Cic boots, loads channelsBySlug = [#a, #b].
  //   2. Operator joins #c via slash command from another grappa
  //      session OR via a fresh tab; server broadcasts
  //      `channels_changed` on the user-topic — but cic's WS is
  //      mid-reconnect and the broadcast is best-effort fan-out (no
  //      live subscriber).
  //   3. Cic reconnects; phoenix.js auto-rejoins user-topic +
  //      already-known channel topics. #c is NOT in cic's
  //      `channelsBySlug` snapshot (the channels_changed broadcast
  //      that would have triggered refetchChannels() was dropped),
  //      so the channels-loop never iterates over #c, never joins
  //      its topic, never receives any of #c's traffic.
  //   4. Operator sends from #c; cic shows nothing.
  //
  // The defensive refetch on every reconnect-induced socket-open
  // event closes the gap. It is REDUNDANT with `channels_changed`
  // for live operation (the broadcast handles changes during the
  // session); it is the only safety net for the disconnect window.
  //
  // Mirror reasoning for `networks` — operators rarely add networks
  // mid-session but the cost of refetch is one HTTP request and the
  // alternative is silent absence.
  //
  // The `prev` filter masks the initial open transition (createEffect
  // fires once at registration with prev=undefined; the bootstrap path
  // already triggered the resource fetch). Only the
  // post-disconnect transitions ("error" → "open" or "connecting" →
  // "open") trigger the refetch — the steady-state "open" → "open"
  // arm is a no-op.
  createEffect(
    on(
      () => socketHealth().state,
      (state, prev) => {
        if (prev === undefined) return;
        if (state !== "open") return;
        // Transition into "open" from any non-open state means the
        // socket re-established. Force-refresh so the channels-loop
        // re-iterates with fresh server-side truth.
        refetchNetworks();
        refetchChannels();
        // #159 item 3 — cic-driven reconnect catch-up. The refetch above
        // only re-runs the channels-loop, which SKIPS every key already in
        // `joined` (`joined.has(key) continue`), so a channel that was
        // subscribed before the drop is backfilled ONLY if phoenix.js's
        // auto-rejoin re-fires that channel's join "ok" recHook. A rejoin
        // that never reaches "ok" (wedged `errored`/`joining`, or a
        // per-channel fan-out severed without a socket close) leaves the
        // gap unhealed until a full reload. Drive `refreshScrollback` for
        // EVERY already-joined key directly so reconnect recovery no
        // longer depends on the per-channel rejoin completing.
        //
        // #1593 — this sweep is a FLOOR, never the whole recovery: it runs
        // before any per-channel topic has rejoined, so rows written after
        // its own query and before that subscription goes live are outside
        // it by construction. The join-ok refresh is what covers that span,
        // and on CI the two are ~30 ms apart — i.e. they reliably overlap on
        // the wire. What keeps the later one alive is `refreshScrollback`'s
        // QUEUE, not its in-flight guard: the guard used to DROP it, which is
        // exactly how #1593's row was lost. Do not "optimise" the queue away.
        for (const joinedKey of joined.keys()) {
          const decoded = decodeChannelKey(joinedKey);
          if (decoded !== null) {
            void refreshScrollback(decoded.slug, decoded.name);
          }
        }
      },
    ),
  );

  // #200 e2e seam — expose the LIVE set of per-channel WS subscription
  // keys so a Playwright spec can assert own-PART tears the subscription
  // down (the leak fix). A getter over the real `joined` Map (not a
  // mirrored Set) so it can never drift from the actual bookkeeping.
  // Production never reads it; same window-seam convention as
  // `__cic_dmListenerReady` and `__cic_suppressChannelDeliveryForTests`.
  if (typeof window !== "undefined") {
    window.__cic_joinedTopicKeys = () => Array.from(joined.keys());
  }
});

// #159 E2E hook — silence live `phx.on("event")` delivery for a SINGLE
// per-channel topic while the socket and every OTHER channel stay live.
// Sibling to `__cic_dropSocketForTests` (socket.ts), but for the
// socket-STAYS-open per-channel gap class the socket-drop hook can't
// reproduce (a drop rejoins every channel and heals via join-ok). Lets
// a Playwright spec prove that activation/visibility/reconnect freshness
// re-fetch — NOT a socket rejoin — closes the gap. Production never
// calls these, so `suppressedDeliveryKeys` stays empty.
declare global {
  interface Window {
    __cic_suppressChannelDeliveryForTests?: (slug: string, name: string) => void;
    __cic_resumeChannelDeliveryForTests?: (slug: string, name: string) => void;
    // #200 e2e seam — live per-channel WS subscription keys (see the
    // getter installed inside the createRoot above).
    __cic_joinedTopicKeys?: () => string[];
  }
}

if (typeof window !== "undefined") {
  window.__cic_suppressChannelDeliveryForTests = (slug, name) => {
    suppressedDeliveryKeys.add(channelKey(slug, name));
  };
  window.__cic_resumeChannelDeliveryForTests = (slug, name) => {
    suppressedDeliveryKeys.delete(channelKey(slug, name));
  };
}
